/**
 * Matching a cited figure against the way the page prints it (#12090).
 *
 * A figure reaches the viewer normalised: a table cell holds `12082` for a
 * document that prints `12,082M`, a reply says `2.81` where the page says
 * `+2.81%`, a column stores `1.7` against a printed `1.70%`. The exact match
 * missed every one of those and fell through to the passage highlight, which
 * for a value cited from an extracted table is the whole table — so several
 * rows lit up instead of the cell.
 *
 * `figureRegex` recognises a figure and builds the one pattern the page's
 * spellings of that number match: the phrase exactly as written, or the same
 * number with any thousands separator or none, an optional currency sign, a
 * decimal point or comma, trailing zeros after the point either way round,
 * and an optional unit or percent suffix. The pattern is bounded so the
 * digits never match inside a longer number: `12082` finds `12,082M` and not
 * `120820`, and `1.7` finds `1.70%` and not `1.75%`. A sign the phrase
 * carries is kept — `-2.5` is not `+2.5` — and a sign the phrase lacks may
 * not appear on the page.
 *
 * The literal spelling is always an alternative too, so the pattern built
 * from a phrase matches that phrase: a figure the regrouping cannot
 * reproduce (`555 1234`) still finds itself. `rankFigureHits` then orders
 * what the page offered so the phrase's own spelling beats a looser hit,
 * and a hit carrying the phrase's unit beats one without it: `20.0%` must
 * find `20.0%` or `20%` before the `20` in "page 20 of 44".
 *
 * A phrase that is not a figure gets `null`, and the caller keeps its plain
 * text match — this only widens what a NUMBER can match, never text.
 *
 * Lives beside the vendored viewer as a twin copy, like `anchorOccurrence.js`:
 * the pdf.js viewer is a static asset in an iframe and cannot import from the
 * bundle. `figureMatch.test.ts` runs its suite against BOTH copies.
 */

// What may sit between thousands groups on a page: a comma, an apostrophe
// (Swiss), a plain, non-breaking or narrow space (European). Up to two so a
// group split across two text runs ("12," then "082") still joins. The
// price is that two unrelated numbers in adjacent cells ("12  082") read as
// one figure; the exact-spelling tier in `rankFigureHits` is what keeps
// that from winning over the real hit in practice.
const SEPARATOR_CHARS = ",'\\u00a0\\u202f ";
const GROUP_SEPARATOR = `[${SEPARATOR_CHARS}]{0,2}`;
const CURRENCY = "[$\\u20ac\\u00a3\\u00a5]";
const MINUS = "[-\\u2212]";
// Suffixes an answer strips and a document keeps. Longest first so the
// alternation takes "bps" before "b", "mm" before "m"; never the start of a
// word, so "$4,973.29 million" highlights the figure, not "$4,973.29 m"; and
// a single-letter unit only when attached, so "3 x 4 grid" is not "3 x".
const UNIT = "(?:(?:\\s?(?:%|bps|bn|mm)|[mkxb])(?![a-z]))";

// The whole phrase must be one figure: optional opening paren, currency and
// sign in either order, the digits with separators and an optional fraction,
// an optional unit, optional closing paren. Anything else is not a figure.
const FIGURE_SHAPE = new RegExp(
  `^(\\()?\\s*(?:${CURRENCY}\\s*)?(${MINUS}|\\+)?\\s*(?:${CURRENCY}\\s*)?` +
    "(\\d[\\d.,'\\u00a0\\u202f ]*)" +
    `\\s*(${UNIT})?\\s*\\)?$`,
  "i"
);

// A minus that is a sign: a true minus always, a hyphen unless a digit sits
// right before it. A hyphen right after a digit is a range dash: "5-10%"
// cites "10%", "2019-2024" cites "2024". With a space before it the hyphen
// is the sign of the next number, since a text layer joins adjacent table
// cells with one space: "3.1 -2.5%" is never a positive "2.5". A spaced
// range ("5 - 10%") needs no rule: a space sits before the figure.
const SIGN_MINUS_BEFORE = "(?<!(?<!\\d)-)(?<!\\u2212)";
// Not preceded by a digit, a decimal mark, a plus, an opening paren (with
// or without a space after it), a sign minus, or a currency sign. The
// currency is refused so a match starts at the currency itself, where the
// paren and the minus before it are checked: "12082" never finds the
// "12,082" inside the accounting negative "($12,082)", nor "5" the "5" in
// "-$5". The same holds with a space after the currency, as a text layer
// that splits "$" from its digits prints it: "($ 12,082)", "-$ 5".
const CURRENCY_CHARS = "$\\u20ac\\u00a3\\u00a5";
const SPACED_CURRENCY_BEFORE = `(?<![${CURRENCY_CHARS}]\\s)`;
const BOUNDARY_BEFORE =
  `(?<![\\d.,+(${CURRENCY_CHARS}])(?<!\\(\\s)` +
  SPACED_CURRENCY_BEFORE +
  SIGN_MINUS_BEFORE;
// The phrase's own spelling may also follow an opening paren when it
// carries a unit: "8%" finds "(8%)". A bare number in parens is an
// accounting negative, so "8" never finds "(8)" nor "( 8 )". A unit does
// not rule out a negative either (a table may print "(8%)" for -8%); that
// reading is the trade for finding a parenthetical "(8%)" in prose.
const literalBoundaryBefore = (figure) =>
  figure.unit
    ? `(?<![\\d.,+${CURRENCY_CHARS}])` +
      SPACED_CURRENCY_BEFORE +
      SIGN_MINUS_BEFORE
    : BOUNDARY_BEFORE;
// A decimal mark: a point, or a comma that is not followed by exactly three
// digits — that comma separates thousands ("1,700"), it does not start a
// fraction ("1,7%").
const DECIMAL_MARK = "(?:\\.|,(?!\\s?\\d{3}(?![\\d])))";
// What may not follow a figure's digits: more digits, a decimal mark and
// digits (with the space either side a split run may carry), or a
// three-digit group after a thousands separator, built from the very class
// GROUP_SEPARATOR joins with so the guard refuses exactly the longer numbers
// the matcher itself would read. This sits right after the digits: a unit,
// a percent sign or a closing paren can never be followed by the rest of
// the same number. The price is a figure read as the head of an unrelated
// number that happens to follow it: "page 20 150 items" no longer cites
// "20", "12, 5 and 7" no longer cites "12".
const TRAILING_BOUNDARY =
  "(?![\\d])(?!\\s{0,2}[.,]\\s{0,2}\\d)" +
  `(?![${SEPARATOR_CHARS}]{1,2}\\d{3}(?![\\d]))`;

const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * The integer digits and the fraction digits of a figure's numeric core,
 * or null when the core is not a number. `12,082` and `12082` both read as
 * ("12082", ""); `1,5` reads as ("1", "5") because a comma followed by
 * anything but a three-digit group is a decimal comma; `1.70` reads as
 * ("1", "7") because trailing zeros carry no information.
 */
const splitFigure = (core) => {
  const compact = core.replace(/[\u00a0\u202f' ]/g, "");
  let integer = compact;
  let fraction = "";
  const dot = compact.lastIndexOf(".");
  if (dot !== -1) {
    integer = compact.slice(0, dot);
    fraction = compact.slice(dot + 1);
  } else {
    const comma = compact.lastIndexOf(",");
    if (comma !== -1 && compact.length - comma - 1 !== 3) {
      integer = compact.slice(0, comma);
      fraction = compact.slice(comma + 1);
    }
  }
  integer = integer.replace(/[^\d]/g, "");
  fraction = fraction.replace(/[^\d]/g, "").replace(/0+$/, "");
  if (!integer) return null;
  return { integer, fraction };
};

/** The digits regrouped in threes from the right, a separator allowed
 *  between groups: "12082" -> "12" sep "082". */
const groupedIntegerPattern = (integer) => {
  const groups = [];
  for (let end = integer.length; end > 0; end -= 3) {
    groups.unshift(integer.slice(Math.max(0, end - 3), end));
  }
  return groups.join(GROUP_SEPARATOR);
};

/** What the phrase says about itself, for building and ranking. */
const parseFigure = (phrase) => {
  const text = `${phrase ?? ""}`.trim();
  const shape = text.match(FIGURE_SHAPE);
  if (!shape) return null;
  const [, paren, sign, core, unit] = shape;
  const parts = splitFigure(core);
  if (!parts) return null;
  return {
    text,
    negative: Boolean(paren) || (sign !== undefined && sign !== "+"),
    unit: unit ? unit.toLowerCase() : "",
    ...parts,
  };
};

/**
 * A regex matching every spelling of `phrase` a document might print, or
 * `null` when `phrase` is not a single figure. Case-insensitive, so
 * `12,082m` and `12,082M` both match.
 */
export const figureRegex = (phrase) => {
  const figure = parseFigure(phrase);
  if (!figure) return null;

  const integer = groupedIntegerPattern(figure.integer);
  // A text layer sometimes breaks a figure around its decimal mark
  // ("1. 9x"); a space either side of the mark is allowed. A comma is a
  // decimal mark only when what follows is not a thousands group: "100%"
  // is not "$100,000" and "1.7" is not "1,700".
  const fraction = figure.fraction
    ? `\\s?${DECIMAL_MARK}\\s?${figure.fraction}0*`
    : `(?:\\s?${DECIMAL_MARK}\\s?0+)?`;
  // A negative phrase needs the page's negative — a minus or accounting
  // parens; a non-negative phrase may carry a plus and never a minus.
  const sign = figure.negative ? `(?:${MINUS}\\s?|\\(\\s?)` : "(?:\\+\\s?)?";
  const close = figure.negative ? "\\)?" : "";
  const tolerant =
    // Not preceded by a digit, a decimal mark, a sign or an opening paren:
    // "12082" is not the tail of "112082" nor the fraction of "0.12082", and
    // "5" does not start inside "-5%" — a sign is matched, never skipped. A
    // range dash is not a sign (BOUNDARY_BEFORE).
    BOUNDARY_BEFORE +
    `(?:${CURRENCY}\\s?)?${sign}(?:${CURRENCY}\\s?)?` +
    integer +
    fraction +
    // Not followed by more digits, nor by a decimal mark and digits: "12,082"
    // is not the head of "12,082.5" nor of "120820" — nor of "100, 000" or
    // "12 082" when a text layer splits the group off with a space. Checked
    // before the unit, so "20.0% 500" still reads "20.0%" whole.
    TRAILING_BOUNDARY +
    // A digit right after the unit is not this figure either: "3x4 grid"
    // cites "3", never "3x".
    `${UNIT}?${close}(?![\\d])`;
  // The phrase's own spelling as well, whatever the regrouping made of it —
  // second in the alternation, so where both match the fuller tolerant
  // reading ("-1.13%" over "-1.13") is what gets highlighted. Its tail gets
  // the same guard only when the spelling ends in a digit.
  const literalTail = /\d$/.test(figure.text) ? TRAILING_BOUNDARY : "(?![\\d])";
  const literal = `${literalBoundaryBefore(figure)}${escapeRegExp(figure.text)}${literalTail}`;
  return new RegExp(`${tolerant}|${literal}`, "i");
};

/** Every hit of `regex` in `text` as `{ index, length, text }`, in page
 *  order. `regex` is rebuilt global so the caller's instance is untouched. */
export const figureHits = (regex, text) => {
  if (!regex) return [];
  const global = new RegExp(regex.source, `${regex.flags}g`);
  const hits = [];
  for (let m = global.exec(text); m !== null; m = global.exec(text)) {
    hits.push({ index: m.index, length: m[0].length, text: m[0] });
  }
  return hits;
};

const normalizeSpelling = (text) =>
  text.toLowerCase().replace(/[\s\u00a0\u202f]+/g, "");

/**
 * `hits` split into preference tiers, best first: the phrase's own spelling;
 * then hits that carry the unit or the sign the phrase carries (so `20.0%`
 * prefers the page's `20%` over its `20`); then everything else. Each tier
 * keeps page order, so the anchoring that runs over a tier still means
 * "inside the passage, else after it, else first".
 */
export const rankFigureHits = (hits, phrase) => {
  const figure = parseFigure(phrase);
  if (!figure || !hits.length) return [hits];
  const wanted = normalizeSpelling(figure.text);
  const exact = [];
  const shaped = [];
  const rest = [];
  for (const hit of hits) {
    const spelling = normalizeSpelling(hit.text);
    if (spelling === wanted) {
      exact.push(hit);
      continue;
    }
    const hasUnit = figure.unit ? spelling.endsWith(figure.unit) : true;
    const hasSign = figure.negative ? /^[-−(]/.test(spelling) : true;
    (hasUnit && hasSign ? shaped : rest).push(hit);
  }
  return [exact, shaped, rest].filter((tier) => tier.length);
};
